import axios from "axios";
import { API_URL, FALLBACK_API_URL } from "../config/apiConfig";

/**
 * Enhanced API utility with retry logic and better error handling
 * This helps resolve production API connection issues
 */

let currentApiUrl = API_URL;

/**
 * Create an axios instance with interceptors for better error handling
 */
const createApiInstance = (baseURL) => {
  const instance = axios.create({
    baseURL,
    timeout: 15000, // 15 second timeout
    headers: {
      "Content-Type": "application/json",
    },
  });

  // Request interceptor to add auth token
  instance.interceptors.request.use(
    (config) => {
      const token =
        localStorage.getItem("token") ||
        localStorage.getItem("vendorToken") ||
        localStorage.getItem("driverToken") ||
        localStorage.getItem("adminToken");

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      return config;
    },
    (error) => {
      console.error("❌ Request interceptor error:", error);
      return Promise.reject(error);
    },
  );

  // Response interceptor for better error handling
  instance.interceptors.response.use(
    (response) => {
      return response;
    },
    (error) => {
      console.error(
        `❌ API Error: ${error.response?.status || "Network"} ${error.config?.url}`,
        error,
      );

      if (error.response?.status === 401) {
        // Handle unauthorized - clear tokens
        localStorage.removeItem("token");
        localStorage.removeItem("vendorToken");
        localStorage.removeItem("driverToken");
        localStorage.removeItem("adminToken");
        localStorage.removeItem("user");
        localStorage.removeItem("vendorData");
        localStorage.removeItem("driver");
        localStorage.removeItem("admin");

        // Redirect to login if not already there
        if (!window.location.pathname.includes("login")) {
          window.location.href = "/login";
        }
      }

      return Promise.reject(error);
    },
  );

  return instance;
};

// Create main API instance
let apiInstance = createApiInstance(currentApiUrl);

/**
 * Make an API request with automatic fallback and retry logic
 */
export const apiRequest = async (config) => {
  try {
    const response = await apiInstance(config);
    return response;
  } catch (error) {

    // If primary API fails and we haven't tried fallback yet
    if (currentApiUrl !== FALLBACK_API_URL) {
      try {
        currentApiUrl = FALLBACK_API_URL;
        apiInstance = createApiInstance(currentApiUrl);

        const fallbackResponse = await apiInstance(config);
        return fallbackResponse;
      } catch (fallbackError) {
        console.error("❌ Fallback API also failed:", fallbackError);
        throw fallbackError;
      }
    }

    throw error;
  }
};

/**
 * Helper methods for common HTTP operations
 */
export const api = {
  get: (url, config = {}) => apiRequest({ ...config, method: "GET", url }),
  post: (url, data, config = {}) =>
    apiRequest({ ...config, method: "POST", url, data }),
  put: (url, data, config = {}) =>
    apiRequest({ ...config, method: "PUT", url, data }),
  delete: (url, config = {}) =>
    apiRequest({ ...config, method: "DELETE", url }),
  patch: (url, data, config = {}) =>
    apiRequest({ ...config, method: "PATCH", url, data }),
};

/**
 * Get current API URL being used
 */
export const getCurrentApiUrl = () => currentApiUrl;

/**
 * Reset to primary API URL (useful for testing)
 */
export const resetToPrimaryApi = () => {
  currentApiUrl = API_URL;
  apiInstance = createApiInstance(currentApiUrl);
};

/**
 * Test API connectivity
 */
export const testApiConnection = async () => {
  try {
    const response = await apiRequest({
      method: "GET",
      url: "/api-test",
      timeout: 5000,
    });

    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export default api;
